import {MultipartBody} from '@microsoft/kiota-abstractions';
import {defineStore} from 'pinia';
import {ref} from 'vue';
import type {FilterModel, IServerSideGetRowsRequest} from 'ag-grid-enterprise';
import type {
  AgGridClientQuery,
  Allotment,
  AllotmentApproval,
  AllotmentApproveRequest,
  AllotmentCreateRequest,
  AllotmentUpdateRequest,
  AllotmentWithFare,
  BusinessRule,
  BusinessRuleTestResponse,
  FileStreamResult,
  HccAllotment,
  PagingResponseOfAllotmentWithFare,
  PagingResponseOfHccAllotment,
  PagingResponseOfTourOperatorAllotment,
  Role,
  StagingAllotment,
  TourOperatorAllotment,
  TourOperatorAllotmentUpdateRequest,
  TourOperatorAllotmentVerification,
} from '@/api/client/models/index.ts';
import { InsertSelectedAllotmentsRequestBuilderRequestsMetadata } from '@/api/client/stagingAllotment/insertSelectedAllotments/index.ts';
import type {DatasourceResponse} from '@/models/DatasourceResponse.ts';
import {useApiClient} from '@/hooks/api-hook.ts';
import {useCurrentUserStore} from '@/stores/current-user-store.ts';
import {
  AllotmentRolesConstants as Roles
} from '@/stores/definitions.js';
import {mapFilterModelToApi, mapSortModelToApi} from '@/util/grid-to-api-mappers.ts';
import {dateFormatter, toDateOnly, toDateOnlyString} from "@/util/date-formatting.ts";
import {toNumberOrNull} from "@/util/number-formatting.ts";
import {createFileDownloadResponseHandlerOption, downloadArrayBufferFile, downloadFile} from '@/util/file-download.ts';

type UploadFile = Blob & {
  name?: string;
  type?: string;
};

type LegacyBusinessRule = Omit<BusinessRule, 'fromDate' | 'toDate'> & {
  fromDate?: string | null;
  toDate?: string | null;
};

type AllotmentGridRow = Omit<AllotmentWithFare, 'departureDate'> & {
  departureDate?: string | null;
};

type TourOperatorAllotmentGridRow = Omit<TourOperatorAllotment, 'departureDate'> & {
  departureDate?: string | null;
};

type HccAllotmentGridRow = Omit<HccAllotment, 'departureDate'> & {
  departureDate?: string | null;
};

type ApprovalRow = Omit<AllotmentApproval, 'departureDate'> & {
  departureDate?: string | null;
};

type BatchVerificationRow = Omit<TourOperatorAllotmentVerification, 'date' | 'testedRules'> & {
  date?: string | null;
  testedRules?: LegacyBusinessRule[] | null;
};

type ExportSortModelItem = {
  colId?: string | null;
  sort?: string | null;
};

function normalizeBusinessRule(rule: Partial<BusinessRule> | null | undefined): LegacyBusinessRule | undefined {
  if (!rule) {
    return undefined;
  }

  return {
    ...rule,
    fromDate: toDateOnlyString(rule.fromDate),
    toDate: toDateOnlyString(rule.toDate),
  };
}

function normalizeAllotmentRow<T extends {
  departureDate?: unknown
}>(entry: T): Omit<T, 'departureDate'> & { departureDate?: string | null } {
  return {
    ...entry,
    departureDate: toDateOnlyString(entry.departureDate),
  };
}

function normalizeAllotmentRows(
  rows: AllotmentWithFare[] | TourOperatorAllotment[] | HccAllotment[] | null | undefined
): Array<AllotmentGridRow | TourOperatorAllotmentGridRow | HccAllotmentGridRow> {
  return (rows ?? []).map(entry => normalizeAllotmentRow(entry));
}

function normalizeApprovalRows(rows: AllotmentApproval[] | null | undefined): ApprovalRow[] {
  return (rows ?? []).map(entry => normalizeAllotmentRow(entry));
}

function normalizeVerificationRows(rows: TourOperatorAllotmentVerification[] | null | undefined): BatchVerificationRow[] {
  return (rows ?? []).map(entry => ({
    ...entry,
    date: toDateOnlyString(entry.date),
    matchingRuleIds: entry.matchingRuleIds ?? [],
    testedRules: entry.testedRules?.map(rule => normalizeBusinessRule(rule) ?? {}) ?? [],
  }));
}

function normalizeRoles(rows: Role[] | null | undefined): Record<string, string> {
  return Object.fromEntries(
    (rows ?? [])
      .filter((entry): entry is Role & {
        id: number;
        name: string
      } => entry.id !== null && entry.id !== undefined && !!entry.name)
      .map(entry => [String(entry.id), entry.name])
  );
}

async function createMultipartBody(input: UploadFile): Promise<MultipartBody> {
  const multipartBody = new MultipartBody();
  multipartBody.addOrReplacePart(
    'formFile',
    input.type || 'text/csv',
    await input.arrayBuffer(),
    undefined,
    input.name || 'allotments.csv'
  );
  return multipartBody;
}

function normalizeFareAmounts(value: unknown): AllotmentCreateRequest['fareAmounts'] {
  if (!Array.isArray(value)) {
    return undefined;
  }

  return value.map(entry => {
    if (!entry || typeof entry !== 'object') {
      return entry;
    }

    const fareAmount = entry as Record<string, unknown>;
    const fareBasisCodeId = fareAmount.fareBasisCodeId ?? fareAmount.farebasisCodeId;

    return {
      ...entry,
      brandingId: toNumberOrNull(fareAmount.brandingId),
      farebasisCodeId: toNumberOrNull(fareBasisCodeId),
    };
  }) as AllotmentCreateRequest['fareAmounts'];
}

function buildCreateRequest(data: Record<string, unknown>): AllotmentCreateRequest {
  return {
    ...data,
    contractSize: toNumberOrNull(data.contractSize),
    departureDate: toDateOnly(data.departureDate),
    fareAmounts: normalizeFareAmounts(data.fareAmounts),
    seasonId: toNumberOrNull(data.seasonId),
    size: toNumberOrNull(data.size),
    tourOperatorId: toNumberOrNull(data.tourOperatorId),
  } as AllotmentCreateRequest;
}

function toVersionString(value: unknown): string | null | undefined {
  if (value === undefined || value === null || typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' && Number.isSafeInteger(value)) {
    return String(value);
  }

  throw new TypeError('Version must be a string or safe integer.');
}

function buildUpdateRequest(data: Record<string, unknown>): AllotmentUpdateRequest {
  return {
    ...data,
    contractSize: toNumberOrNull(data.contractSize),
    departureDate: toDateOnly(data.departureDate),
    fareAmounts: normalizeFareAmounts(data.fareAmounts),
    id: toNumberOrNull(data.id),
    nonPoolingSize: toNumberOrNull(data.nonPoolingSize),
    seasonId: toNumberOrNull(data.seasonId),
    tourOperatorId: toNumberOrNull(data.tourOperatorId),
    version: toVersionString(data.version),
  } as AllotmentUpdateRequest;
}

function ensureInsertSelectedAllotmentsRequestBodySerialization(): void {
  // Kiota omits this marker for scalar-array request bodies, so the proxy skips the JSON body.
  InsertSelectedAllotmentsRequestBuilderRequestsMetadata.post!.requestBodySerializer ??= 'number';
}

function buildChangeRequest(data: Record<string, unknown>): TourOperatorAllotmentUpdateRequest {
  return {
    ...data,
    id: toNumberOrNull(data.id),
    size: toNumberOrNull(data.size),
    version: toVersionString(data.version),
  } as TourOperatorAllotmentUpdateRequest;
}

function buildGridQuery(request: IServerSideGetRowsRequest, fieldMap: Record<string, string>): AgGridClientQuery {
  return {
    startRow: request.startRow,
    endRow: request.endRow,
    sortModel: mapSortModelToApi(request.sortModel, fieldMap),
    filterModel: mapFilterModelToApi((request.filterModel ?? {}) as FilterModel, fieldMap),
  };
}

function buildExportQuery(
  filterModel: FilterModel,
  sortModel: ExportSortModelItem[],
  fieldMap: Record<string, string>
): AgGridClientQuery {
  return {
    sortModel: (sortModel ?? [])
      .filter(sort => sort.sort !== null && sort.sort !== undefined)
      .map(sort => ({
        colId: fieldMap[sort.colId ?? ''] || sort.colId,
        sort: String(sort.sort).toLowerCase() === 'desc' ? 'Desc' : 'Asc',
      })),
    filterModel: mapFilterModelToApi(filterModel, fieldMap),
  };
}

export const useAllotmentsStore = defineStore('allotments', () => {
  const apiClient = useApiClient();
  const currentUserStore = useCurrentUserStore();

  const loading = ref<boolean>(true);
  const roles = ref<Record<string, string> | null>(null);
  const count = ref<number | null>(null);
  const rejectionsCount = ref<number | null>(null);
  const result = ref<AllotmentWithFare | null>(null);
  const showOnlyActive = ref<boolean>(true);
  const showOnlyUpToDate = ref<boolean>(true);
  const shouldLoadFinalData = ref<boolean>(false);

  const fieldMap: Record<string, string> = {
    tourOperatorId: 'fare.touroperatorid',
    seasonId: 'fare.seasonId',
    origin: 'fare.origin',
    destination: 'fare.destination',
    flightNumber: 'fare.flightnumber',
    month: 'departuredate.month',
    fares: 'sold',
  };

  function getRoleById(id: number | string | null | undefined): string | null {
    if (id === null || id === undefined) {
      return null;
    }

    return roles.value?.[String(id)] ?? null;
  }

  async function resolveRole(): Promise<number | undefined> {
    if (currentUserStore.currentUser?.allotmentRoleId !== null && currentUserStore.currentUser?.allotmentRoleId !== undefined) {
      return Number(currentUserStore.currentUser.allotmentRoleId);
    }

    const currentUser = await currentUserStore.get();
    return currentUser?.allotmentRoleId ?? undefined;
  }

  async function get(
    request: IServerSideGetRowsRequest
  ): Promise<DatasourceResponse<AllotmentGridRow | TourOperatorAllotmentGridRow | HccAllotmentGridRow>> {
    loading.value = true;

    try {
      const role = await resolveRole();
      const query = buildGridQuery(request, fieldMap);

      const response:
        | PagingResponseOfAllotmentWithFare
        | PagingResponseOfTourOperatorAllotment
        | PagingResponseOfHccAllotment
        | undefined = role === Roles.TOUR_OPERATOR
        ? await apiClient.allotment.getForTourOperator.post(query, {
          queryParameters: {
            showUpToDate: showOnlyUpToDate.value,
          },
        })
        : role === Roles.HCC_READONLY
          ? await apiClient.allotment.getForHcc.post(query)
          : shouldLoadFinalData.value
            ? await apiClient.allotment.getFinal.post(query)
            : await apiClient.allotment.getPath.post(query, {
              queryParameters: {
                isActive: showOnlyActive.value,
                showUpToDate: showOnlyUpToDate.value,
              },
            });

      const rows = normalizeAllotmentRows(response?.rows);
      rejectionsCount.value = rows.filter(entry => Boolean((entry as TourOperatorAllotmentGridRow).isRequestRejected)).length;

      return {
        total: response?.total ?? 0,
        rows,
      };
    } finally {
      loading.value = false;
    }
  }

  async function getById(id: number): Promise<AllotmentGridRow | TourOperatorAllotmentGridRow | undefined> {
    loading.value = true;

    try {
      const role = await resolveRole();
      const response = role === Roles.TOUR_OPERATOR
        ? await apiClient.allotment.getForTourOperator.byId(id).get()
        : await apiClient.allotment.byId(id).get();

      return response ? normalizeAllotmentRow(response) : undefined
    } finally {
      loading.value = false;
    }
  }

  async function refreshById(id: number): Promise<void> {
    loading.value = true;

    return apiClient
      .allotment
      .refresh
      .post({queryParameters: {id}})
      .finally(() => {
        loading.value = false
      })
  }

  async function getPendingApproval(): Promise<Record<string, ApprovalRow[]>> {
    loading.value = true;

    try {
      const response = await apiClient.allotment.getForApproval.get();
      const rows = normalizeApprovalRows(response);
      count.value = rows.length;

      return rows.reduce<Record<string, ApprovalRow[]>>((accumulator, currentValue) => {
        const key = String(currentValue.tourOperatorId ?? '');
        if (!accumulator[key]) {
          accumulator[key] = [currentValue];
        } else {
          accumulator[key].push(currentValue);
        }
        return accumulator;
      }, {});
    } finally {
      loading.value = false;
    }
  }

  async function add(data: Record<string, unknown>): Promise<AllotmentWithFare | undefined> {
    loading.value = true;

    return apiClient
      .allotment
      .post(buildCreateRequest(data))
      .finally(() => loading.value = false)
  }

  async function deleteAllotment(id: number): Promise<void> {
    loading.value = true

    return apiClient
      .allotment
      .delete({queryParameters: {id}})
      .finally(() => {
        loading.value = false
      })
  }

  async function update(data: Record<string, unknown>): Promise<void> {
    loading.value = true;

    return apiClient
      .allotment
      .put(buildUpdateRequest(data))
      .finally(() => {
        loading.value = false
      })
  }

  async function downloadPassengerListCsv(allotmentId: number): Promise<FileStreamResult | undefined> {
    loading.value = true;

    try {
      const response = await apiClient.allotment.byId(allotmentId).downloadPassengerListCsv.get({
        options: [createFileDownloadResponseHandlerOption()],
      }) as unknown as FileStreamResult | undefined;

      return downloadFile(response, `PassengerList-${allotmentId}-${dateFormatter(new Date()).timestamp}.csv`);
    } finally {
      loading.value = false;
    }
  }

  async function upload(input: UploadFile): Promise<StagingAllotment[]> {
    const response = await apiClient.stagingAllotment.createFromCsv.post(await createMultipartBody(input));
    if (!response) {
      throw new Error('Allotment upload returned no staging rows.');
    }
    return response;
  }

  async function uploadTourOperator(input: UploadFile): Promise<BatchVerificationRow[]> {
      const response = await apiClient.allotment.verifyBusinessRule.createFromCsv.post(await createMultipartBody(input));
      return normalizeVerificationRows(response);
  }

  async function commit(data: number[]): Promise<Allotment[]> {
    loading.value = true;

    try {
      ensureInsertSelectedAllotmentsRequestBodySerialization();
      const response = await apiClient.stagingAllotment.insertSelectedAllotments.post(
        data.map(entry => Number(entry))
      );

      return response ?? [];
    } finally {
      loading.value = false;
    }
  }

  async function approve({id, version}: {
    id: number;
    version: string
  }): Promise<void> {
    loading.value = true;

    try {
      await apiClient.allotment.approve.post({
        queryParameters: {
          id,
          version,
        },
      });

    } finally {
      loading.value = false;
    }
  }

  async function batchApprove(allotments: AllotmentApproveRequest[]): Promise<void> {
    loading.value = true;

    try {
      const request = allotments.map(entry => ({
        ...entry,
        id: toNumberOrNull(entry.id),
        version: toVersionString(entry.version),
      })) as AllotmentApproveRequest[];

      await apiClient.allotment.approveBatch.post(request);
    } finally {
      loading.value = false;
    }
  }

  async function reject(
    {id, version, description}: { id: number; version: string; description: string }
  ): Promise<void> {
    loading.value = true;

    try {
      await apiClient.allotment.reject.post({
        queryParameters: {
          description,
          id,
          version,
        },
      });

    } finally {
      loading.value = false;
    }
  }

  async function verify(id: number,newSize: number | string): Promise<BusinessRuleTestResponse> {
    loading.value = true;

    try {
      const parsedNewSize = toNumberOrNull(newSize);
      if (parsedNewSize == null) {
        throw new Error('New allotment size must be a valid number.');
      }

      const response = await apiClient.allotment.verifyBusinessRule.post({
        queryParameters: { id, newSize: parsedNewSize },
      });
      if (!response) {
        throw new Error('Allotment verification returned no response.');
      }
      return response;
    } finally {
      loading.value = false;
    }
  }

  async function cancelChangeRequest(allotmentId: number): Promise<void> {
    loading.value = true;

    try {
      await apiClient.allotment.cancelUpdateAllotmentSize.post({
        queryParameters: {
          id: allotmentId,
        },
      });

    } finally {
      loading.value = false;
    }
  }

  async function commitChangeRequests(data: Record<string, unknown>): Promise<void> {
    loading.value = true;

    try {
      await apiClient.allotment.updateAllotmentSize.post(buildChangeRequest(data));
    } finally {
      loading.value = false;
    }
  }

  async function getRoles(): Promise<Record<string, string>> {
    loading.value = true;

    try {
      const response = await apiClient.allotmentRole.get();
      roles.value = normalizeRoles(response);
      return roles.value;
    } finally {
      loading.value = false;
    }
  }

  async function exportCsv(
    filterModel: FilterModel,
    sortModel: ExportSortModelItem[]
  ): Promise<ArrayBuffer | undefined> {
    const role = await resolveRole();
    const query = buildExportQuery(filterModel, sortModel, fieldMap);

    const response = role === Roles.TOUR_OPERATOR
      ? await apiClient.allotment.createCsvForTourOperator.post(query, {
        queryParameters: {
          showUpToDate: showOnlyUpToDate.value,
        },
      })
      : role === Roles.HCC_READONLY
        ? await apiClient.allotment.createCsvForHcc.post(query)
        : await apiClient.allotment.createCsv.post(query, {
          queryParameters: {
            isActive: showOnlyActive.value,
            isFinal: shouldLoadFinalData.value,
            showUpToDate: showOnlyUpToDate.value,
          },
        });

    return downloadArrayBufferFile(
      response,
      `Allotments-${dateFormatter(new Date()).timestamp}.csv`
    );
  }

  return {
    loading,
    roles,
    count,
    rejectionsCount,
    result,
    shouldLoadFinalData,
    showOnlyUpToDate,
    showOnlyActive,
    getRoleById,
    get,
    getById,
    refreshById,
    getPendingApproval,
    add,
    deleteAllotment,
    update,
    downloadPassengerListCsv,
    upload,
    uploadTourOperator,
    commit,
    approve,
    batchApprove,
    reject,
    verify,
    cancelChangeRequest,
    commitChangeRequests,
    getRoles,
    exportCsv,
  };
});
